import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ApexOptions } from 'apexcharts';

@Injectable({
  providedIn: 'root'
})
export class PlantStatusService {

  private isBrowser: boolean;
  private chart: any | null = null; // ApexCharts instance

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  async loadChart(): Promise<void> {
    if (!this.isBrowser) return;

    try {
      const ApexCharts = (await import('apexcharts')).default;

      const chartElement = document.querySelector('#plant_status_graph') as HTMLElement | null;
      if (!chartElement) {
        console.warn('PlantStatusService: element #plant_status_graph not found');
        return;
      }

      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }

      const options: ApexOptions = {
        series: [3, 1, 1],
        chart: {
          height: 300,
          type: 'donut'
        },
        labels: ['Online', 'Abnormal', 'Offline'],
        legend: {
          offsetY: 0,
          fontSize: '14px',
          position: 'bottom',
          horizontalAlign: 'center',
          labels: {
            colors: '#919aa3'
          },
          itemMargin: {
            horizontal: 12,
            vertical: 7
          },
          formatter: (label: string, opts: any) => {
            const series = opts?.w?.globals?.series;
            const val = Array.isArray(series)
              ? series[opts.seriesIndex]
              : null;

            if (typeof val === 'number' && !isNaN(val)) {
              return `${label} ${val}`;
            }
            return label;
          }
        },
        dataLabels: {
          enabled: false,
          style: {
            fontSize: '14px'
          },
          dropShadow: {
            enabled: false
          }
        },
        colors: ['#B4DE9A', '#FF9994', '#B9DCF8'],
        tooltip: {
          y: {
            formatter: (val: number | null | undefined) => {
              if (typeof val === 'number' && !isNaN(val)) {
                return `${val} ${val === 1 ? 'Plant' : 'Plants'}`;
              }
              return '';
            }
          }
        },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                value: {
                  show: true,
                  fontSize: '16px',
                  color: '#333',
                  formatter: (val: number | string) => {
                    const num = typeof val === 'number'
                      ? val
                      : Number(val);
                    return Number.isFinite(num) ? num.toString() : '';
                  }
                },
                total: {
                  show: true,
                  label: 'Total Plants',
                  fontSize: '14px',
                  color: '#919aa3',
                  formatter: (w: any) => {
                    const totals = w?.globals?.seriesTotals;
                    if (Array.isArray(totals)) {
                      const total = totals.reduce(
                        (a: number, b: number) => a + (Number.isFinite(b) ? b : 0),
                        0
                      );
                      return total.toString();
                    }
                    return '';
                  }
                }
              }
            }
          }
        }
      };

      this.chart = new ApexCharts(chartElement, options);
      this.chart.render();

    } catch (error) {
      console.error('Error loading ApexCharts (PlantStatus):', error);
    }
  }
  destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
